import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api/client";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { Fuel, Zap, MapPin } from "lucide-react";
import LocationPicker from "@/components/LocationPicker";

const QUICK_QTY = [1000, 3000, 6000];

export default function BookOrder() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { data: cust } = useQuery({
    queryKey: ["me-customer"], enabled: !!user?.customer_id,
    queryFn: () => api.get(`/customers/${user.customer_id}`).then((r) => r.data),
  });

  const [siteId, setSiteId] = useState("");
  const [qty, setQty] = useState(1000);
  const [scheduled, setScheduled] = useState("");
  const [emergency, setEmergency] = useState(false);
  const [notes, setNotes] = useState("");
  const [location, setLocation] = useState({ lat: null, lng: null, address: "" });

  React.useEffect(() => {
    if (cust?.sites?.length && !siteId) setSiteId(cust.sites[0].id);
  }, [cust?.sites, siteId]);

  // Whenever the selected site changes, pre-fill the location picker with its saved
  // coordinates + address. Customers can then confirm or adjust the pin before placing.
  React.useEffect(() => {
    const s = (cust?.sites || []).find((x) => x.id === siteId);
    if (!s) return;
    const validCoords = Number(s.lat) && Number(s.lng);
    setLocation({
      lat: validCoords ? Number(s.lat) : null,
      lng: validCoords ? Number(s.lng) : null,
      address: [s.address, s.city].filter(Boolean).join(", "),
    });
  }, [siteId, cust?.sites]);

  const create = useMutation({
    mutationFn: (payload) => api.post("/orders", payload).then((r) => r.data),
    onSuccess: async (order) => {
      toast.success(`Order ${order.code} created!`);
      // If wallet didn't fully cover the total, launch Razorpay for the
      // remainder immediately so the customer pays in the same flow.
      const remainder = Number(order.remainder_amount || 0);
      if (remainder > 0.01) {
        try {
          await openRazorpayCheckout({
            amount: remainder,
            purpose: "order_payment",
            orderId: order.id,
            description: `Order ${order.code} — remaining balance`,
            prefill: { name: user?.name || "", email: user?.email || "" },
            onSuccess: () => {
              toast.success("Payment received — order fully paid");
              nav(`/portal/track/${order.id}`);
            },
            onDismiss: () => {
              toast.info(`Order ${order.code} placed. Complete the pending ₹${remainder.toLocaleString("en-IN")} anytime from the Invoices tab.`);
              nav(`/portal/track/${order.id}`);
            },
          });
        } catch (e) {
          // Razorpay might not be configured (preview env). Order was already
          // saved with the wallet portion applied — just navigate.
          toast.warning("Online payment gateway not configured — remainder can be settled later.");
          nav(`/portal/track/${order.id}`);
        }
      } else {
        nav(`/portal/track/${order.id}`);
      }
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  const submit = () => {
    if (!location.lat || !location.lng) {
      toast.error("Please set a delivery location pin so we can share it with the driver.");
      return;
    }
    if (!location.address?.trim()) {
      toast.error("Please enter the complete delivery address.");
      return;
    }
    // Wallet is always drained first; if it fully covers we use "wallet",
    // otherwise "online" — the backend handles the actual split.
    const effectiveMethod = walletCovers ? "wallet" : "online";
    create.mutate({
      customer_id: user.customer_id,
      site_id: siteId,
      quantity_litres: qty,
      scheduled_at: scheduled || new Date().toISOString(),
      emergency,
      notes,
      payment_method: effectiveMethod,
      // Location is now MANDATORY on every order — shared with driver + staff on dispatch.
      override_lat: location.lat,
      override_lng: location.lng,
      override_address: location.address.trim(),
    });
  };

  const rate = cust?.price_per_litre || 95;
  const subtotal = qty * rate;
  const surchargePct = emergency ? 5 : 0;
  const surchargeAmt = Math.round(subtotal * surchargePct) / 100;
  const total = subtotal + surchargeAmt;

  const walletBal = Number(cust?.wallet_balance || 0);
  const walletCovers = walletBal >= total;

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="book-order-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Book diesel delivery</h1>
        <p className="text-sm text-muted-foreground">Get diesel delivered to your site.</p>
      </div>

      <Card>
        <CardHeader><CardTitle>Order details</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label>Delivery site</Label>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger data-testid="book-site"><SelectValue placeholder="Select site" /></SelectTrigger>
              <SelectContent>
                {(cust?.sites || []).map((s) => (<SelectItem key={s.id} value={s.id}>{s.name} — {s.address}, {s.city}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border-2 border-primary/20 bg-primary/[0.02] p-3" data-testid="book-location-picker">
            <div className="mb-2 flex items-center gap-2">
              <MapPin size={16} className="text-primary" />
              <div className="text-sm font-semibold">Delivery location</div>
              <span className="text-[11px] text-primary">*</span>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">
              Confirm or adjust the exact spot where the tanker should stop. The driver and dispatcher will see this pin.
            </p>
            <LocationPicker value={location} onChange={setLocation} />
          </div>

          <div>
            <Label>Quantity (litres) <span className="text-primary">*</span></Label>
            <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {QUICK_QTY.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setQty(q)}
                  data-testid={`qty-preset-${q}`}
                  className={`rounded-lg border-2 py-2.5 text-sm font-bold transition-all ${
                    qty === q
                      ? "border-primary bg-primary text-primary-foreground shadow-md"
                      : "border-border bg-background hover:border-primary/50 hover:bg-primary/[0.03]"
                  }`}>
                  {q.toLocaleString("en-IN")} L
                </button>
              ))}
              <div className="col-span-3 sm:col-span-1">
                <Input
                  type="number"
                  min="1"
                  max="6000"
                  inputMode="numeric"
                  value={qty || ""}
                  onChange={(e) => setQty(Number(e.target.value) || 0)}
                  placeholder="Custom"
                  className="h-full min-h-[42px] text-center font-bold"
                  data-testid="book-qty"
                />
              </div>
            </div>
            {qty > 6000 && (
              <div className="mt-2 text-xs text-status-cancelled" data-testid="qty-over-limit-hint">
                ⚠ Maximum 6,000 L per order (single-bowser trip). For larger requirements, split into multiple orders.
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label>Scheduled at (optional)</Label>
              <Input type="datetime-local" value={scheduled} onChange={(e) => setScheduled(e.target.value)} data-testid="book-scheduled" />
            </div>
            <div className="flex items-end">
              <div className="flex items-center gap-3 rounded-lg border p-3 w-full">
                <Zap size={16} className="text-primary" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Emergency delivery</div>
                  <div className="text-xs text-muted-foreground">SLA: 60 minutes · +5% surcharge</div>
                </div>
                <Switch checked={emergency} onCheckedChange={setEmergency} data-testid="book-emergency" />
              </div>
            </div>
          </div>

          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Site access instructions, contact person, etc." data-testid="book-notes" />
          </div>

          {/* Payment method selector — postpaid is not offered here; it's assigned by admin/accounts.
             Wallet is always drained first (up to available balance); the remainder (if any) is
             collected via the chosen method. */}
          <div className="space-y-2">
            <Label>Payment method</Label>
            {walletCovers ? (
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <div className="font-bold">Pay from Wallet</div>
                    <div className="text-xs text-muted-foreground">Balance ₹{walletBal.toLocaleString("en-IN")} covers the total.</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Wallet debit</div>
                    <div className="font-bold text-primary">₹{total.toLocaleString("en-IN")}</div>
                  </div>
                </div>
              </div>
            ) : walletBal > 0 ? (
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
                <div className="text-xs text-muted-foreground">
                  Wallet ₹{walletBal.toLocaleString("en-IN")} doesn&apos;t cover the full ₹{total.toLocaleString("en-IN")} — we&apos;ll auto-split:
                </div>
                <div className="flex items-center justify-between rounded-md bg-background/60 p-2 text-sm">
                  <div className="font-semibold">Wallet debit</div>
                  <div className="font-bold text-primary">₹{walletBal.toLocaleString("en-IN")}</div>
                </div>
                <div className="flex items-center justify-between rounded-md bg-background/60 p-2 text-sm">
                  <div className="font-semibold">Online (Razorpay) — remainder</div>
                  <div className="font-bold">₹{(total - walletBal).toLocaleString("en-IN")}</div>
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-border/60 bg-background p-3 text-sm">
                <div className="font-semibold">Online payment (Razorpay)</div>
                <div className="text-xs text-muted-foreground">Wallet balance is ₹0. You&apos;ll be redirected to Razorpay checkout after placing.</div>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Estimated total</div>
            <div className="mt-1 font-display text-3xl font-black text-primary" data-testid="book-total">
              ₹{total.toLocaleString("en-IN")}
            </div>
            <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              <div>{qty}L × ₹{rate}/L = ₹{subtotal.toLocaleString("en-IN")}</div>
              {emergency && (
                <div className="text-orange-600" data-testid="book-surcharge-line">
                  + Emergency delivery ({surchargePct}%): ₹{surchargeAmt.toLocaleString("en-IN")}
                </div>
              )}
              <div className="text-[10px] italic">(excl. GST)</div>
            </div>
          </div>

          <Button className="w-full pill-btn" size="lg" onClick={submit} disabled={!siteId || !qty || qty > 6000 || create.isPending} data-testid="btn-book-submit">
            <Fuel size={16} className="mr-2" />
            {create.isPending ? "Placing order..." : "Place order"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
